import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Turnstile } from '@marsidev/react-turnstile';
import { colors, fonts, layout, spacing, gradients } from './theme';
import { supabase } from './lib/supabase';
import PortalHeader from './PortalHeader';
import PortalFooter from './PortalFooter';
import PortalNav from './components/PortalNav';
import SkeletonLoader from './components/SkeletonLoader';
import EmptyState from './components/EmptyState';
import Button from './components/Button';
import Input from './components/Input';
import Card from './components/Card';
import Table from './components/Table';
import Modal from './components/Modal';
import FileUploadZone from './components/FileUploadZone';
import SearchBar from './components/SearchBar';
import DocumentPreview from './components/DocumentPreview';
import Icon from './components/Icon';
import EntityTypeSelect from './components/EntityTypeSelect';
import { retryOperation, sanitizeFilename, sanitizeInput } from './utils/helpers';
import { showActionableError } from './utils/errorMessages';
import { validateClientProfile, validateEmailField } from './utils/validation';
import { useKeyboardShortcuts } from './utils/useKeyboardShortcuts';
import { DOCUMENT_TYPES } from './constants/documents';

const REQUIRE_PASSWORD_CONFIRM_FOR_PROFILE_SAVE = true;

const styles = {
  container: {
    maxWidth: layout.containerMaxWidth,
    margin: '0 auto',
    padding: layout.pagePadding,
    fontFamily: fonts.body,
    backgroundColor: colors.background,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    flex: 1,
  },
  header: {
    backgroundColor: colors.white,
    padding: spacing.lg,
    marginBottom: spacing.xl,
    borderRadius: layout.borderRadius,
    boxShadow: layout.boxShadow,
  },
  guidanceBox: {
    background: gradients.blueToGreenSubtle,
    borderLeft: `4px solid ${colors.blue}`,
    padding: spacing.lg,
    borderRadius: layout.borderRadius,
    marginBottom: spacing.xl,
  },
  filterBar: {
    display: 'flex',
    gap: spacing.md,
    marginBottom: spacing.md,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  badge: {
    display: 'inline-block',
    fontFamily: fonts.heading,
    fontSize: '0.75rem',
    fontWeight: 500,
    padding: '0.25rem 0.5rem',
    borderRadius: '999px',
    backgroundColor: colors.gray100,
    color: colors.midGray,
  },
  select: {
    fontFamily: fonts.body,
    fontSize: '1rem',
    height: '2.25rem',
    padding: '0.5rem',
    border: `1px solid ${colors.borderColor}`,
    borderRadius: layout.borderRadius,
    minWidth: '150px',
    boxSizing: 'border-box',
    backgroundColor: colors.white,
    transition: 'all 0.2s ease',
  },
};

// Get Cloudflare Turnstile sitekey from environment variable
const TURNSTILE_SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY || '0x4AAAAAACAz8HfmFvKdLrWZ';

function Portal() {
  /** True during password-reset flows so we don't show the generic "email confirmed" toast while recovering. */
  const passwordRecoveryPendingRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const portalSection = (() => {
    const p = location.pathname;
    if (p.startsWith('/documents')) return 'documents';
    if (p.startsWith('/company')) return 'company';
    if (p.startsWith('/billing')) return 'billing';
    if (p.startsWith('/request-services')) return 'services';
    return 'home';
  })();

  const [user, setUser] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [filings, setFilings] = useState([]);
  const [filingsLoading, setFilingsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [signingUp, setSigningUp] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [title, setTitle] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [tag, setTag] = useState('');
  const [customDate, setCustomDate] = useState('');
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const [clientProfile, setClientProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [entityType, setEntityType] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaveCooldownSeconds, setProfileSaveCooldownSeconds] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});
  const [showConfirmPasswordModal, setShowConfirmPasswordModal] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [sendingResetEmail, setSendingResetEmail] = useState(false);
  const [completingPasswordRecovery, setCompletingPasswordRecovery] = useState(false);
  const [recoveryPassword, setRecoveryPassword] = useState('');
  const [recoveryPasswordConfirm, setRecoveryPasswordConfirm] = useState('');
  const [recoveryPasswordError, setRecoveryPasswordError] = useState('');
  const [updatingRecoveryPassword, setUpdatingRecoveryPassword] = useState(false);

  // Owner information
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [ownerAddressLine1, setOwnerAddressLine1] = useState('');
  const [ownerAddressLine2, setOwnerAddressLine2] = useState('');
  const [ownerCity, setOwnerCity] = useState('');
  const [ownerState, setOwnerState] = useState('');
  const [ownerZip, setOwnerZip] = useState('');

  // Business information
  const [businessAddressLine1, setBusinessAddressLine1] = useState('');
  const [businessAddressLine2, setBusinessAddressLine2] = useState('');
  const [businessCity, setBusinessCity] = useState('');
  const [businessState, setBusinessState] = useState('');
  const [businessZip, setBusinessZip] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [ein, setEin] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');

  const [filterType, setFilterType] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');

  const [isAdmin, setIsAdmin] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [filingToDelete, setFilingToDelete] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  // Folder management
  const [folders, setFolders] = useState([]);
  const [selectedFolderId] = useState(null); // Used in uploadFile, but setter not needed
  const [filterFolderId] = useState('all'); // Used in filteredAndSortedFilings, but setter not needed

  const [selectedFilings, setSelectedFilings] = useState([]);
  const [uploadFormOpen, setUploadFormOpen] = useState(false);
  const [serviceFormLoaded, setServiceFormLoaded] = useState(false);
  const [showServiceForm, setShowServiceForm] = useState(false);

  // Edit document state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingFiling, setEditingFiling] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDocumentType, setEditDocumentType] = useState('');
  const [editTag, setEditTag] = useState('');
  const [editCustomDate, setEditCustomDate] = useState('');
  const [editSelectedFolderId, setEditSelectedFolderId] = useState(null);
  const [updatingFiling, setUpdatingFiling] = useState(false);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);

  // Keyboard shortcuts - must be called before any conditional returns
  useKeyboardShortcuts(
    {
      'escape': () => {
        if (editModalOpen) {
          setEditModalOpen(false);
          setEditingFiling(null);
        }
        if (previewModalOpen) {
          setPreviewModalOpen(false);
          setPreviewFile(null);
        }
        if (deleteModalOpen) {
          setDeleteModalOpen(false);
          setFilingToDelete(null);
        }
        if (uploadFormOpen) {
          setUploadFormOpen(false);
        }
      },
      'ctrl+k': (e) => {
        e.preventDefault();
        // Focus search bar
        const searchInput = document.querySelector('input[placeholder*="Search"]');
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      },
      'cmd+k': (e) => {
        e.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Search"]');
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      },
      'ctrl+u': (e) => {
        e.preventDefault();
        if (!uploadFormOpen) {
          setUploadFormOpen(true);
          setTimeout(() => {
            const titleInput = document.querySelector('input[placeholder*="Document title"]');
            if (titleInput) {
              titleInput.focus();
            }
          }, 100);
        }
      },
      'cmd+u': (e) => {
        e.preventDefault();
        if (!uploadFormOpen) {
          setUploadFormOpen(true);
          setTimeout(() => {
            const titleInput = document.querySelector('input[placeholder*="Document title"]');
            if (titleInput) {
              titleInput.focus();
            }
          }, 100);
        }
      },
      'ctrl+/': (e) => {
        e.preventDefault();
        toast.info(
          <div style={{ lineHeight: 1.8 }}>
            <strong>Keyboard Shortcuts:</strong><br />
            Ctrl/Cmd + K - Focus search<br />
            Ctrl/Cmd + U - Open upload form<br />
            Escape - Close modals<br />
            Ctrl/Cmd + / - Show this help
          </div>,
          { duration: 6000 }
        );
      },
      'cmd+/': (e) => {
        e.preventDefault();
        toast.info(
          <div style={{ lineHeight: 1.8 }}>
            <strong>Keyboard Shortcuts:</strong><br />
            Ctrl/Cmd + K - Focus search<br />
            Ctrl/Cmd + U - Open upload form<br />
            Escape - Close modals<br />
            Ctrl/Cmd + / - Show this help
          </div>,
          { duration: 6000 }
        );
      },
    },
    !!user && !showServiceForm,
    [user, uploadFormOpen, editModalOpen, previewModalOpen, deleteModalOpen, showServiceForm]
  );

  const resendConfirmationEmail = useCallback(async () => {
    if (!email) {
      toast.error('Please enter your email address first');
      return;
    }

    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email,
      options: {
        emailRedirectTo: window.location.origin,
      }
    });

    if (error) {
      if (error.message.toLowerCase().includes('rate limit')) {
        toast.error('Too many requests. Please wait a few minutes before requesting another email.');
      } else if (error.message.toLowerCase().includes('already confirmed')) {
        toast.error('This email is already confirmed. You can sign in.');
      } else {
        toast.error(error.message || 'Failed to resend confirmation email. Please try again.');
      }
    } else {
      toast.success('Confirmation email sent! Please check your inbox.', {
        duration: 6000,
      });
    }
  }, [email]);

  useEffect(() => {
    // Handle auth redirect errors from URL hash
    const handleAuthRedirect = () => {
      const hash = window.location.hash;
      if (hash) {
        const params = new URLSearchParams(hash.substring(1));
        const error = params.get('error');
        const errorDescription = params.get('error_description');
        const linkType = params.get('type');

        // Implicit-flow recovery links include type=recovery before the client consumes the hash.
        if (!error && linkType === 'recovery') {
          passwordRecoveryPendingRef.current = true;
          setCompletingPasswordRecovery(true);
        }

        if (error) {
          // Decode the error description
          const decodedDescription = errorDescription 
            ? decodeURIComponent(errorDescription.replace(/\+/g, ' '))
            : '';
          
          // Show user-friendly error messages
          if (error === 'access_denied') {
            if (errorDescription?.includes('expired') || errorDescription?.includes('otp_expired')) {
              toast.error(
                (t) => (
                  <div>
                    <div style={{ marginBottom: '8px' }}>
                      This link has expired (signup confirmation or password reset).
                    </div>
                    <div style={{ marginBottom: '12px', fontSize: '0.875rem', opacity: 0.95 }}>
                      If you were resetting your password, use <strong>Forgot password</strong> on the login page to send a new link.
                    </div>
                    <button
                      onClick={() => {
                        toast.dismiss(t.id);
                        resendConfirmationEmail();
                      }}
                      style={{
                        background: 'transparent',
                        border: '1px solid currentColor',
                        color: 'inherit',
                        padding: '4px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                      }}
                    >
                      Resend signup confirmation email
                    </button>
                  </div>
                ),
                {
                  duration: 10000,
                }
              );
            } else if (errorDescription?.includes('invalid') || errorDescription?.includes('invalid_token')) {
              toast.error(
                (t) => (
                  <div>
                    <div style={{ marginBottom: '8px' }}>
                      This link is invalid or could not be used (signup confirmation or password reset).
                    </div>
                    <div style={{ marginBottom: '12px', fontSize: '0.875rem', opacity: 0.95 }}>
                      If you were resetting your password, use <strong>Forgot password</strong> on the login page.
                    </div>
                    <button
                      onClick={() => {
                        toast.dismiss(t.id);
                        resendConfirmationEmail();
                      }}
                      style={{
                        background: 'transparent',
                        border: '1px solid currentColor',
                        color: 'inherit',
                        padding: '4px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                      }}
                    >
                      Resend signup confirmation email
                    </button>
                  </div>
                ),
                {
                  duration: 10000,
                }
              );
            } else {
              toast.error(decodedDescription || 'Email confirmation failed. Please try again.', {
                duration: 8000,
              });
            }
          } else {
            toast.error(decodedDescription || 'Authentication error. Please try again.', {
              duration: 8000,
            });
          }
          
          // Clear the hash from URL
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      }
    };

    handleAuthRedirect();

    const getSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        setUser(data.session.user);
        const admin = data.session.user?.user_metadata?.role === 'admin';
        setIsAdmin(admin);
        await activateDormantAccount(data.session.user.email);
        loadFilings(data.session.user.id);
        loadClientProfile(data.session.user.id, data.session.user.email);
        loadFolders(data.session.user.id);
      } else {
        setClientProfile(null);
        setProfileLoading(false);
      }
    };
    getSession();

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        passwordRecoveryPendingRef.current = true;
        setCompletingPasswordRecovery(true);
      }
      setUser(session?.user ?? null);
      if (session?.user) {
        const admin = session.user?.user_metadata?.role === 'admin';
        setIsAdmin(admin);
        (async () => {
          if (event === 'SIGNED_IN') {
            await activateDormantAccount(session.user.email);
          }
          loadFilings(session.user.id);
          await loadClientProfile(session.user.id, session.user.email);
          loadFolders(session.user.id);

          if (event === 'SIGNED_IN' && session.user.email_confirmed_at) {
            applyApprovedPendingUpdates(session.user.email, session.user.id);
            if (!passwordRecoveryPendingRef.current) {
              toast.success('Email confirmed successfully! You are now signed in.');
            }
          }
        })();
      } else {
        setClientProfile(null);
        setProfileLoading(false);
      }
    });

    return () => listener.subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resendConfirmationEmail]);

  // Check if user is logged in but email not confirmed
  useEffect(() => {
    if (user && !user.email_confirmed_at && !isAdmin) {
      // Show warning but don't block access completely
      // Users can still see the portal but should confirm email
      const hasShownWarning = sessionStorage.getItem('email_confirmation_warning_shown');
      if (!hasShownWarning) {
        toast.error(
          (t) => (
            <div>
              <div style={{ marginBottom: '8px' }}>Please confirm your email address to access all portal features.</div>
              <button
                onClick={() => {
                  toast.dismiss(t.id);
                  resendConfirmationEmail();
                }}
                style={{
                  background: 'transparent',
                  border: '1px solid currentColor',
                  color: 'inherit',
                  padding: '4px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                Resend confirmation email
              </button>
            </div>
          ),
          {
            duration: 10000,
          }
        );
        sessionStorage.setItem('email_confirmation_warning_shown', 'true');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin, resendConfirmationEmail]);

  // Load JotForm embed handler script for service form when it's shown
  useEffect(() => {
    if (!user || !showServiceForm || serviceFormLoaded) return;

    // Check if script already exists
    const existingScript = document.querySelector('script[src="https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js"]');
    
    if (existingScript) {
      // Script already loaded, just initialize once
      setServiceFormLoaded(true);
      if (window.jotformEmbedHandler) {
        // Use requestAnimationFrame to ensure iframe is fully rendered
        requestAnimationFrame(() => {
          setTimeout(() => {
            window.jotformEmbedHandler("iframe[id='JotFormIFrame-252943654936065']", "https://form.jotform.com/");
          }, 500);
        });
      }
      return;
    }

    // Load the script only once
    const script = document.createElement('script');
    script.src = 'https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js';
    script.async = true;
    script.onload = () => {
      setServiceFormLoaded(true);
      if (window.jotformEmbedHandler) {
        // Use requestAnimationFrame to ensure iframe is fully rendered
        requestAnimationFrame(() => {
          setTimeout(() => {
            window.jotformEmbedHandler("iframe[id='JotFormIFrame-252943654936065']", "https://form.jotform.com/");
          }, 500);
        });
      }
    };
    document.body.appendChild(script);
  }, [user, showServiceForm, serviceFormLoaded]);

  useEffect(() => {
    setShowServiceForm(portalSection === 'services');
  }, [portalSection]);

  const loadBilling = useCallback(async () => {
    if (!user) return;
    setBillingLoading(true);
    setBillingError('');
    try {
      const { data, error } = await supabase.functions.invoke('stripe-billing', {
        body: { email: user.email },
      });
      if (error) {
        let detail = error.message || 'Billing request failed';
        try {
          // Supabase FunctionsHttpError often includes the response as context
          const ctx = error.context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body?.error) detail = body.error;
            else if (body?.detail) detail = `${detail}: ${body.detail}`;
          }
        } catch (_) {
          // ignore parse failures
        }
        throw new Error(detail);
      }
      if (data?.error) throw new Error(data.error);
      setBillingData(data);
    } catch (err) {
      setBillingError(err.message || 'Unable to load billing right now.');
      setBillingData(null);
    } finally {
      setBillingLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (portalSection === 'billing' && user) {
      loadBilling();
    }
  }, [portalSection, user, loadBilling]);


  // Cooldown after profile save to limit rapid repeated updates
  useEffect(() => {
    if (profileSaveCooldownSeconds <= 0) return;
    const t = setInterval(() => {
      setProfileSaveCooldownSeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [profileSaveCooldownSeconds]);

  // After returning from email confirmation link: refetch profile and show success
  useEffect(() => {
    if (!user?.id) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('profile_confirmed') !== '1') return;
    loadClientProfile(user.id).then(() => {
      toast.success('Your profile has been updated.');
    });
    window.history.replaceState({}, '', window.location.pathname || window.location.origin);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only when user.id and URL param are present; loadClientProfile is intentionally omitted to avoid re-running when its ref changes
  }, [user?.id]);

  const activateDormantAccount = async (userEmail) => {
    if (!userEmail) return null;
    try {
      const { data, error } = await supabase.functions.invoke('activate-dormant-account', {
        body: { email: userEmail },
      });
      if (error) throw error;
      if (data?.activated) {
        toast.success('Welcome back — your existing records are now linked to this account.');
      }
      return data;
    } catch (err) {
      console.warn('Dormant activation check:', err);
      return null;
    }
  };

  const loadFilings = async (userId) => {
    if (!userId) {
      console.warn('loadFilings called with undefined userId');
      setFilingsLoading(false);
      return;
    }
    setFilingsLoading(true);
    const { data, error } = await supabase
      .from('filings')
      .select('*')
      .eq('user_id', userId)
      .order('uploaded_at', { ascending: false });
    if (error) {
      console.error(error);
      toast.error('Failed to load filings');
    } else {
      setFilings(data || []);
    }
    setFilingsLoading(false);
  };

  const loadClientProfile = async (userId, userEmail = null) => {
    setProfileLoading(true);
    setProfileError('');

    try {
      await retryOperation(async () => {
        let { data, error } = await supabase
          .from('clients')
          .select('*')
          .eq('user_id', userId)
          .is('deleted_at', null)
          .maybeSingle();

        if (error) {
          throw error;
        }

        // Auto-create an incomplete stub so users can join without a company
        if (!data) {
          const stub = {
            user_id: userId,
            business_name: null,
            entity_type: null,
            login_email: userEmail || null,
            profile_status: 'incomplete',
            updated_at: new Date().toISOString(),
          };
          const { data: created, error: createError } = await supabase
            .from('clients')
            .upsert(stub, { onConflict: 'user_id' })
            .select('*')
            .single();
          if (createError) {
            // Column may not exist yet before migration — retry minimal stub
            const { data: minimal, error: minimalError } = await supabase
              .from('clients')
              .upsert(
                {
                  user_id: userId,
                  business_name: null,
                  entity_type: null,
                },
                { onConflict: 'user_id' }
              )
              .select('*')
              .single();
            if (minimalError) throw createError;
            data = minimal;
          } else {
            data = created;
          }
        } else if (userEmail && !data.login_email) {
          await supabase
            .from('clients')
            .update({ login_email: userEmail })
            .eq('id', data.id);
          data = { ...data, login_email: userEmail };
        }

        setClientProfile(data);
        if (data) {
          setBusinessName(data.business_name || '');
          setEntityType(data.entity_type || '');
          // Owner information
          setOwnerName(data.owner_name || '');
          setOwnerEmail(data.owner_email || '');
          setOwnerPhone(data.owner_phone || '');
          setOwnerAddressLine1(data.owner_address_line1 || '');
          setOwnerAddressLine2(data.owner_address_line2 || '');
          setOwnerCity(data.owner_city || '');
          setOwnerState(data.owner_state || '');
          setOwnerZip(data.owner_zip || '');
          // Business information
          setBusinessAddressLine1(data.business_address_line1 || '');
          setBusinessAddressLine2(data.business_address_line2 || '');
          setBusinessCity(data.business_city || '');
          setBusinessState(data.business_state || '');
          setBusinessZip(data.business_zip || '');
          setBusinessPhone(data.business_phone || '');
          setBusinessEmail(data.business_email || '');
          setEin(data.ein || '');
          setRegistrationNumber(data.registration_number || '');
          // Registered agent info is loaded but not editable by users (admin-only)
        }
      });
    } catch (error) {
      console.error('Profile load error:', error);
      // Check if table doesn't exist (PostgreSQL error code 42P01)
      if (error.code === '42P01' || error.message?.includes('does not exist')) {
        const errorMsg = 'Clients table not found. Please create the table in Supabase.';
        setProfileError(errorMsg);
        toast.error(errorMsg);
      } else if (error.code === 'PGRST116' || error.message?.includes('permission denied') || error.message?.includes('row-level security')) {
        const errorMsg = 'Permission denied. Please check RLS policies in Supabase.';
        setProfileError(errorMsg);
        showActionableError(error, () => loadClientProfile(userId, userEmail), 'Loading profile');
      } else {
        const errorMsg = `Failed to load profile: ${error.message || 'Unknown error'}`;
        setProfileError(errorMsg);
        showActionableError(error, () => loadClientProfile(userId, userEmail), 'Loading profile');
      }
    } finally {
      setProfileLoading(false);
    }
  };

  const loadFolders = async (userId) => {
    const { data, error } = await supabase
      .from('folders')
      .select('*')
      .eq('user_id', userId)
      .order('name', { ascending: true });

    if (error) {
      // Silently handle missing table or permission errors
      const isTableMissing = error.code === '42P01' || 
                            error.code === 'PGRST116' ||
                            error.message?.includes('does not exist') ||
                            error.message?.includes('permission denied') ||
                            error.message?.includes('relation') ||
                            error.message?.includes('table');
      
      if (isTableMissing) {
        // Table doesn't exist or no permission - silently set empty array
        console.log('Folders table not available yet - this is normal if table not created');
        setFolders([]);
      } else {
        // Other errors - log but don't show toast
        console.error('Folder load error:', error);
        setFolders([]);
      }
    } else {
      setFolders(data || []);
    }
  };

  // Check and apply approved pending updates when user confirms email
  const applyApprovedPendingUpdates = async (userEmail, userId) => {
    try {
      await retryOperation(async () => {
        // Find approved pending updates for this email that don't have user_id yet
        const { data: pendingUpdates, error } = await supabase
          .from('pending_client_updates')
          .select('*')
          .eq('client_email', userEmail.toLowerCase())
          .eq('status', 'approved')
          .is('user_id', null);

        if (error) {
          throw error;
        }

        if (!pendingUpdates || pendingUpdates.length === 0) {
          return; // No pending updates to apply
        }

        console.log(`Found ${pendingUpdates.length} approved pending update(s) to apply`);

        // Process each pending update
        for (const update of pendingUpdates) {
          // Update user_id in pending update
          const { error: updateError } = await supabase
            .from('pending_client_updates')
            .update({ user_id: userId })
            .eq('id', update.id);

          if (updateError) {
            console.error('Error updating pending update:', updateError);
            continue; // Continue with other updates even if one fails
          }

          // Parse mapped_data
          const mappedData = typeof update.mapped_data === 'string' 
            ? JSON.parse(update.mapped_data)
            : update.mapped_data;

          if (mappedData) {
            // Prepare client data
            const clientData = {
              user_id: userId,
              last_info_update: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };

            // Map all fields from mapped_data to clientData
            const fieldMapping = {
              business_name: 'business_name',
              entity_type: 'entity_type',
              ein: 'ein',
              registration_number: 'registration_number',
              owner_name: 'owner_name',
              owner_email: 'owner_email',
              owner_phone: 'owner_phone',
              owner_address_line1: 'owner_address_line1',
              owner_address_line2: 'owner_address_line2',
              owner_city: 'owner_city',
              owner_state: 'owner_state',
              owner_zip: 'owner_zip',
              business_address_line1: 'business_address_line1',
              business_address_line2: 'business_address_line2',
              business_city: 'business_city',
              business_state: 'business_state',
              business_zip: 'business_zip',
              business_phone: 'business_phone',
              business_email: 'business_email',
              registered_agent_name: 'registered_agent_name',
              registered_agent_address_line1: 'registered_agent_address_line1',
              registered_agent_address_line2: 'registered_agent_address_line2',
              registered_agent_city: 'registered_agent_city',
              registered_agent_state: 'registered_agent_state',
              registered_agent_zip: 'registered_agent_zip',
              registered_agent_phone: 'registered_agent_phone',
              registered_agent_email: 'registered_agent_email',
            };

            // Only add fields that have values in mapped_data
            Object.keys(fieldMapping).forEach(key => {
              const mappedKey = fieldMapping[key];
              if (mappedData[mappedKey] !== null && mappedData[mappedKey] !== undefined && mappedData[mappedKey] !== '') {
                clientData[key] = mappedData[mappedKey];
              }
            });

            // Upsert client record
            const { error: upsertError } = await supabase
              .from('clients')
              .upsert(clientData, { onConflict: 'user_id' });

            if (upsertError) {
              console.error('Error upserting client from pending update:', upsertError);
              // Continue with other updates even if one fails
            }
          }
        }

        // Reload client profile to show the new data
        await loadClientProfile(userId);
        toast.success('Your business information has been automatically applied!');
      });
    } catch (error) {
      console.error('Error applying pending updates:', error);
      // Don't show error to user - this is background process
    }
  };

  const getUpdateData = () => {
    const cleanedName = businessName ? sanitizeInput(businessName, 255) : null;
    const cleanedType = entityType ? sanitizeInput(entityType, 100) : null;
    return {
      user_id: user.id,
      business_name: cleanedName,
      entity_type: cleanedType,
      login_email: user.email || null,
      profile_status: cleanedName ? 'active' : 'incomplete',
      owner_name: ownerName ? sanitizeInput(ownerName, 255) : null,
      owner_email: ownerEmail ? sanitizeInput(ownerEmail, 255) : null,
      owner_phone: ownerPhone ? sanitizeInput(ownerPhone, 20) : null,
      owner_address_line1: ownerAddressLine1 ? sanitizeInput(ownerAddressLine1, 255) : null,
      owner_address_line2: ownerAddressLine2 ? sanitizeInput(ownerAddressLine2, 255) : null,
      owner_city: ownerCity ? sanitizeInput(ownerCity, 100) : null,
      owner_state: ownerState ? sanitizeInput(ownerState, 50) : null,
      owner_zip: ownerZip ? sanitizeInput(ownerZip, 20) : null,
      business_address_line1: businessAddressLine1 ? sanitizeInput(businessAddressLine1, 255) : null,
      business_address_line2: businessAddressLine2 ? sanitizeInput(businessAddressLine2, 255) : null,
      business_city: businessCity ? sanitizeInput(businessCity, 100) : null,
      business_state: businessState ? sanitizeInput(businessState, 50) : null,
      business_zip: businessZip ? sanitizeInput(businessZip, 20) : null,
      business_phone: businessPhone ? sanitizeInput(businessPhone, 20) : null,
      business_email: businessEmail ? sanitizeInput(businessEmail, 255) : null,
      ein: ein ? sanitizeInput(ein, 20) : null,
      registration_number: registrationNumber ? sanitizeInput(registrationNumber, 50) : null,
      last_info_update: new Date().toISOString(),
      info_update_requested_at: new Date().toISOString(),
      info_update_requested_by: user.id,
    };
  };

  const performProfileSave = async (updateData) => {
    setSavingProfile(true);
    setProfileError('');
    try {
      const isFirstTime = !clientProfile;
      if (isFirstTime) {
        // First-time setup: create client directly (no email confirmation)
        await retryOperation(async () => {
          const { data, error } = await supabase
            .from('clients')
            .upsert(updateData, { onConflict: 'user_id' })
            .select('*')
            .single();
          if (error) throw error;
          setClientProfile(data);
          if (data) {
            setBusinessName(data.business_name || '');
            setEntityType(data.entity_type || '');
            setOwnerName(data.owner_name || '');
            setOwnerEmail(data.owner_email || '');
            setOwnerPhone(data.owner_phone || '');
            setOwnerAddressLine1(data.owner_address_line1 || '');
            setOwnerAddressLine2(data.owner_address_line2 || '');
            setOwnerCity(data.owner_city || '');
            setOwnerState(data.owner_state || '');
            setOwnerZip(data.owner_zip || '');
            setBusinessAddressLine1(data.business_address_line1 || '');
            setBusinessAddressLine2(data.business_address_line2 || '');
            setBusinessCity(data.business_city || '');
            setBusinessState(data.business_state || '');
            setBusinessZip(data.business_zip || '');
            setBusinessPhone(data.business_phone || '');
            setBusinessEmail(data.business_email || '');
            setEin(data.ein || '');
            setRegistrationNumber(data.registration_number || '');
          }
          toast.success('Profile created. You can update your information anytime from Information on File.');
        });
        return;
      }

      // Existing profile: send review email; changes apply only after they confirm via email
      const redirectUrl = window.location.origin + (window.location.pathname || '');
      const { data, error } = await supabase.functions.invoke('request-profile-update', {
        body: { proposedData: updateData, redirectUrl },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success('Check your email to confirm your changes. Click the button in the email to apply them.');
      setProfileSaveCooldownSeconds(15);
    } catch (error) {
      console.error('Profile save error:', error);
      if (error.code === '42P01' || error.message?.includes('does not exist')) {
        setProfileError('Clients table not found. Please create the table in Supabase.');
        toast.error('Clients table not found.');
      } else if (error.code === 'PGRST116' || error.message?.includes('permission denied') || error.message?.includes('row-level security')) {
        setProfileError('Permission denied. Please check RLS policies in Supabase.');
        showActionableError(error, () => performProfileSave(updateData), 'Saving profile');
      } else {
        setProfileError(error.message || 'Unknown error');
        showActionableError(error, () => performProfileSave(updateData), 'Saving profile');
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const saveClientProfile = async () => {
    if (!user) return;

    const validation = validateClientProfile(
      {
        businessName,
        entityType,
        ownerEmail,
        businessEmail,
        ownerPhone,
        businessPhone,
      },
      { requireCompany: !!(businessName || entityType) }
    );
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      toast.error(Object.values(validation.errors)[0]);
      return;
    }
    setValidationErrors({});

    const isFirstTimeSetup = !clientProfile?.business_name;
    if (REQUIRE_PASSWORD_CONFIRM_FOR_PROFILE_SAVE && clientProfile?.business_name) {
      setShowConfirmPasswordModal(true);
      return;
    }
    await performProfileSave(getUpdateData());
  };

  const handleConfirmPasswordSubmit = async (e) => {
    e?.preventDefault();
    setConfirmPasswordError('');
    if (!confirmPassword.trim()) {
      setConfirmPasswordError('Please enter your password.');
      return;
    }
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: confirmPassword,
      });
      if (error) throw error;
      setShowConfirmPasswordModal(false);
      setConfirmPassword('');
      await performProfileSave(getUpdateData());
    } catch (err) {
      console.error('Confirm password error:', err);
      setConfirmPasswordError(err.message || 'Invalid password. Try again or save without confirming if you use magic link.');
    }
  };

  const handleSaveWithoutConfirm = () => {
    setShowConfirmPasswordModal(false);
    setConfirmPassword('');
    setConfirmPasswordError('');
    performProfileSave(getUpdateData());
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    setDeletingAccount(true);
    try {
      const { data, error } = await supabase.functions.invoke('request-account-deletion', {
        body: {}
      });
      if (error) throw new Error(error.message || 'Request failed');
      if (data?.error) throw new Error(data.error);
      await supabase.auth.signOut();
      toast.success(data?.message || 'Account deleted. You have been signed out.');
      window.location.href = '/';
    } catch (err) {
      console.error('Delete account error:', err);
      toast.error(err.message || 'Could not delete account. Please try again or contact support.');
    } finally {
      setDeletingAccount(false);
    }
  };

  const signUp = async () => {
    // Prevent double submission
    if (signingUp) {
      return;
    }

    // Validate email
    const emailValidation = validateEmailField(email, true);
    if (!emailValidation.isValid) {
      setEmailError(emailValidation.error);
      toast.error(emailValidation.error);
      return;
    }

    if (password.length < 12) {
      toast.error('Password must be at least 12 characters');
      return;
    }
    
    setSigningUp(true);
    setEmailError('');
    
    try {
      // Try with CAPTCHA token if available, otherwise try without
      const authOptions = captchaToken 
        ? { options: { captchaToken } }
        : {};
      
      const { data, error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          emailRedirectTo: window.location.origin,
          ...(authOptions.options || {}),
        }
      });
      
      if (error) {
        // If error mentions CAPTCHA, show specific message
        if (error.message.toLowerCase().includes('captcha')) {
          toast.error('CAPTCHA verification required. Please complete the CAPTCHA above.');
          setCaptchaToken(null);
        } else if (error.message.toLowerCase().includes('email') && error.message.toLowerCase().includes('rate limit')) {
          toast.error('Too many signup attempts. Please wait a few minutes and try again.');
        } else if (error.message.toLowerCase().includes('smtp') || error.message.toLowerCase().includes('email')) {
          toast.error('Email service error. Please contact support or try again later.');
          console.error('Signup error:', error);
        } else {
          toast.error(error.message || 'Signup failed. Please try again.');
          console.error('Signup error:', error);
        }
      } else {
        // Check if email confirmation is required
        if (data.user && !data.user.email_confirmed_at) {
          toast.success('Account created! Check your email to confirm signup.');
        } else {
          toast.success('Account created successfully!');
        }
        setCaptchaToken(null);
        // Clear form
        setEmail('');
        setPassword('');
      }
    } finally {
      setSigningUp(false);
    }
  };

  const signIn = async () => {
    // Prevent double submission
    if (signingIn) {
      return;
    }

    const emailValidation = validateEmailField(email, true);
    if (!emailValidation.isValid) {
      setEmailError(emailValidation.error);
      toast.error(emailValidation.error);
      return;
    }
    setEmailError('');

    setSigningIn(true);
    
    try {
      // Try with CAPTCHA token if available, otherwise try without
      const authOptions = captchaToken 
        ? { options: { captchaToken } }
        : {};
      
      const { error } = await supabase.auth.signInWithPassword({ 
        email, 
        password,
        ...authOptions
      });
      
      if (error) {
        // If error mentions CAPTCHA, show specific message
        if (error.message.toLowerCase().includes('captcha')) {
          toast.error('CAPTCHA verification required. Please complete the CAPTCHA above.');
          setCaptchaToken(null);
        } else {
          toast.error(error.message);
        }
      } else {
        toast.success('Signed in successfully');
        setCaptchaToken(null);
        // User will be set by onAuthStateChange, which will trigger loadFilings
      }
    } finally {
      setSigningIn(false);
    }
  };

  const sendPasswordResetEmail = async () => {
    if (sendingResetEmail) {
      return;
    }

    const emailValidation = validateEmailField(email, true);
    if (!emailValidation.isValid) {
      setEmailError(emailValidation.error);
      toast.error(emailValidation.error);
      return;
    }

    setEmailError('');
    setSendingResetEmail(true);

    try {
      const options = {
        redirectTo: `${window.location.origin}${window.location.pathname || '/'}`,
      };
      if (TURNSTILE_SITEKEY && captchaToken) {
        options.captchaToken = captchaToken;
      }
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), options);

      if (error) {
        if (error.message.toLowerCase().includes('captcha')) {
          toast.error('CAPTCHA verification required. Please complete the CAPTCHA above.');
          setCaptchaToken(null);
        } else {
          toast.error(error.message || 'Could not send reset email. Please try again.');
        }
      } else {
        toast.success('Check your email for a link to reset your password.', { duration: 6000 });
        setShowForgotPassword(false);
        setCaptchaToken(null);
      }
    } finally {
      setSendingResetEmail(false);
    }
  };

  const completePasswordRecovery = async () => {
    if (updatingRecoveryPassword) return;
    setRecoveryPasswordError('');
    if (recoveryPassword.length < 12) {
      setRecoveryPasswordError('Password must be at least 12 characters.');
      return;
    }
    if (recoveryPassword !== recoveryPasswordConfirm) {
      setRecoveryPasswordError('Passwords do not match.');
      return;
    }

    setUpdatingRecoveryPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: recoveryPassword });
      if (error) throw error;
      toast.success('Your password has been updated.');
      setCompletingPasswordRecovery(false);
      setRecoveryPassword('');
      setRecoveryPasswordConfirm('');
      // Allow auth follow-up events (e.g. SIGNED_IN) to settle before turning toast guard off.
      window.setTimeout(() => {
        passwordRecoveryPendingRef.current = false;
      }, 2500);
    } catch (err) {
      console.error('Password recovery error:', err);
      setRecoveryPasswordError(err.message || 'Could not update password. Try again or request a new link.');
    } finally {
      setUpdatingRecoveryPassword(false);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setFilings([]);
    setClientProfile(null);
    setBusinessName('');
    setEntityType('');
    setCompletingPasswordRecovery(false);
    setRecoveryPassword('');
    setRecoveryPasswordConfirm('');
    setRecoveryPasswordError('');
    setUpdatingRecoveryPassword(false);
    passwordRecoveryPendingRef.current = false;
    toast.success('Signed out successfully');
  };

  const uploadFile = async () => {
    if (!files.length || !title) {
      toast.error('Title and file are required.');
      return;
    }

    if (!user) return;

    // Validate tag length
    if (tag && tag.length > 30) {
      toast.error('Tag must be 30 characters or less.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const file = files[0];
      const fileExt = file.name.split('.').pop() || 'file';
      // Sanitize title for filename
      const sanitizedTitle = sanitizeFilename(title || 'document');
      const fileName = `${user.id}/${Date.now()}-${sanitizedTitle}.${fileExt}`;

      // Upload with progress tracking
      const { error: uploadError } = await supabase.storage
        .from('filings')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        toast.error(uploadError.message);
        setUploading(false);
        return;
      }

      setUploadProgress(100);

      // If tag is provided, check if folder exists, if not create it
      let folderIdToUse = selectedFolderId || null;
      if (tag && tag.trim()) {
        const tagTrimmed = sanitizeInput(tag.trim(), 50); // Limit folder name to 50 chars
        // Validate folder name
        if (tagTrimmed.length < 1 || tagTrimmed.length > 50) {
          toast.error('Tag must be between 1 and 50 characters.');
          setUploading(false);
          return;
        }
        // Check if folder with this tag name exists
        const existingFolder = folders.find(f => f.name.toLowerCase() === tagTrimmed.toLowerCase());
        
        if (!existingFolder) {
          // Create new folder with tag name
          const { data: newFolder, error: folderError } = await supabase
            .from('folders')
            .insert({
              user_id: user.id,
              name: tagTrimmed,
              description: `Auto-created from tag: ${tagTrimmed}`,
            })
            .select('*')
            .single();

          if (folderError) {
            console.error('Error creating folder:', folderError);
            toast.error('Failed to create folder for tag. Uploading without folder.');
          } else {
            folderIdToUse = newFolder.id;
            // Reload folders to include the new one
            loadFolders();
            toast.success(`Created folder "${tagTrimmed}" for this document`);
          }
        } else {
          folderIdToUse = existingFolder.id;
        }
      }

      const { error: insertError } = await supabase.from('filings').insert({
        user_id: user.id,
        title,
        file_path: fileName,
        document_type: documentType || null,
        folder_id: folderIdToUse,
        tag: tag && tag.trim() ? tag.trim() : null,
        custom_date: customDate || null,
        source: 'manual',
      });

      if (insertError && /source|schema cache/i.test(insertError.message || '')) {
        const retry = await supabase.from('filings').insert({
          user_id: user.id,
          title,
          file_path: fileName,
          document_type: documentType || null,
          folder_id: folderIdToUse,
          tag: tag && tag.trim() ? tag.trim() : null,
          custom_date: customDate || null,
        });
        if (retry.error) {
          toast.error(retry.error.message);
        } else {
          toast.success('Document uploaded successfully');
          loadFilings(user.id);
          setTitle('');
          setDocumentType('');
          setTag('');
          setCustomDate('');
          setFiles([]);
          setUploadProgress(0);
          setUploadFormOpen(false);
        }
      } else if (insertError) {
        toast.error(insertError.message);
      } else {
        toast.success('Document uploaded successfully');
        loadFilings(user.id);
        setTitle('');
        setDocumentType('');
        setTag('');
        setCustomDate('');
        setFiles([]);
        setUploadProgress(0);
        setUploadFormOpen(false); // Close the upload form after successful upload
      }
    } catch (error) {
      toast.error('Upload failed. Please try again.');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const getDownloadUrl = async (filePath) => {
    const { data, error } = await supabase.storage
      .from('filings')
      .createSignedUrl(filePath, 600);
    if (error) {
      toast.error(error.message);
      return null;
    }
    return data.signedUrl;
  };

  const handleDeleteClick = (filing) => {
    setFilingToDelete(filing);
    setDeleteModalOpen(true);
  };

  const deleteFiling = async () => {
    if (!filingToDelete) return;

    const { error: storageError } = await supabase.storage
      .from('filings')
      .remove([filingToDelete.file_path]);

    if (storageError) {
      toast.error(storageError.message);
      setDeleteModalOpen(false);
      return;
    }

    const { error: dbError } = await supabase
      .from('filings')
      .delete()
      .eq('id', filingToDelete.id);

    if (dbError) {
      toast.error(dbError.message);
    } else {
      toast.success('Document deleted successfully');
      loadFilings(user.id);
    }

    setDeleteModalOpen(false);
    setFilingToDelete(null);
  };

  const getMimeType = (filePath) => {
    const extension = filePath.split('.').pop()?.toLowerCase();
    const mimeTypes = {
      // Images
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'svg': 'image/svg+xml',
      'bmp': 'image/bmp',
      // PDF
      'pdf': 'application/pdf',
      // Text files
      'txt': 'text/plain',
      'csv': 'text/csv',
      'json': 'application/json',
      'xml': 'application/xml',
      'html': 'text/html',
      'css': 'text/css',
      'js': 'text/javascript',
      'md': 'text/markdown',
      // Office documents (may not preview in browser)
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    };
    return mimeTypes[extension] || null;
  };

  const handlePreview = async (filing) => {
    const url = await getDownloadUrl(filing.file_path);
    if (url) {
      const mimeType = getMimeType(filing.file_path);
      setPreviewFile({
        url,
        fileName: filing.title,
        fileType: mimeType,
        fileExtension: filing.file_path.split('.').pop()?.toLowerCase(),
      });
      setPreviewModalOpen(true);
    }
  };

  const handleEditClick = (filing) => {
    setEditingFiling(filing);
    setEditTitle(filing.title || '');
    setEditDocumentType(filing.document_type || '');
    setEditTag(filing.tag || '');
    // Format date for date input (YYYY-MM-DD)
    const dateValue = filing.custom_date 
      ? new Date(filing.custom_date).toISOString().split('T')[0]
      : '';
    setEditCustomDate(dateValue);
    setEditSelectedFolderId(filing.folder_id || null);
    setEditModalOpen(true);
  };

  const updateFiling = async () => {
    if (!editingFiling || !editTitle.trim()) {
      toast.error('Title is required.');
      return;
    }

    if (!user) return;

    // Validate tag length
    if (editTag && editTag.length > 30) {
      toast.error('Tag must be 30 characters or less.');
      return;
    }

    setUpdatingFiling(true);

    try {
      // If tag is provided, check if folder exists, if not create it
      let folderIdToUse = editSelectedFolderId || null;
      if (editTag && editTag.trim()) {
        const tagTrimmed = sanitizeInput(editTag.trim(), 50); // Limit folder name to 50 chars
        // Validate folder name
        if (tagTrimmed.length < 1 || tagTrimmed.length > 50) {
          toast.error('Tag must be between 1 and 50 characters.');
          setUpdatingFiling(false);
          return;
        }
        // Check if folder with this tag name exists
        const existingFolder = folders.find(f => f.name.toLowerCase() === tagTrimmed.toLowerCase());
        
        if (!existingFolder) {
          // Create new folder with tag name
          const { data: newFolder, error: folderError } = await supabase
            .from('folders')
            .insert({
              user_id: user.id,
              name: tagTrimmed,
              description: `Auto-created from tag: ${tagTrimmed}`,
            })
            .select('*')
            .single();

          if (folderError) {
            console.error('Error creating folder:', folderError);
            toast.error('Failed to create folder for tag. Updating without folder.');
          } else {
            folderIdToUse = newFolder.id;
            // Reload folders to include the new one
            loadFolders();
            toast.success(`Created folder "${tagTrimmed}" for this document`);
          }
        } else {
          folderIdToUse = existingFolder.id;
        }
      }

      // Format custom_date properly - convert empty string to null
      const customDateValue = editCustomDate && editCustomDate.trim() 
        ? editCustomDate.trim() 
        : null;

      const { error: updateError } = await supabase
        .from('filings')
        .update({
          title: editTitle.trim(),
          document_type: editDocumentType || null,
          tag: editTag && editTag.trim() ? editTag.trim() : null,
          custom_date: customDateValue,
          folder_id: folderIdToUse,
        })
        .eq('id', editingFiling.id);

      if (updateError) {
        console.error('Update error:', updateError);
        const errorMessage = updateError.message || 'Failed to update document';
        toast.error(errorMessage);
        // Check for specific error types
        if (updateError.code === 'PGRST116' || updateError.message?.includes('permission denied') || updateError.message?.includes('row-level security')) {
          toast.error('Permission denied. Please check RLS policies in Supabase.');
        } else if (updateError.code === '23503') {
          toast.error('Invalid folder reference. Please select a valid folder.');
        } else {
          toast.error(`Update failed: ${errorMessage}`);
        }
      } else {
        toast.success('Document updated successfully');
        loadFilings(user.id);
        setEditModalOpen(false);
        setEditingFiling(null);
        setEditTitle('');
        setEditDocumentType('');
        setEditTag('');
        setEditCustomDate('');
        setEditSelectedFolderId(null);
      }
    } catch (error) {
      console.error('Update failed:', error);
      toast.error(`Update failed: ${error.message || 'Please try again.'}`);
    } finally {
      setUpdatingFiling(false);
    }
  };

  const filteredAndSortedFilings = () => {
    let filtered = [...filings];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (f) =>
          f.title.toLowerCase().includes(query) ||
          (f.document_type && f.document_type.toLowerCase().includes(query))
      );
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter((f) => f.document_type === filterType);
    }

    // Folder filter
    if (filterFolderId !== 'all') {
      if (filterFolderId === 'unfiled') {
        filtered = filtered.filter((f) => !f.folder_id);
      } else {
        filtered = filtered.filter((f) => f.folder_id === parseInt(filterFolderId));
      }
    }

    // Sort
    if (sortOrder === 'newest') {
      filtered.sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
    } else if (sortOrder === 'oldest') {
      filtered.sort((a, b) => new Date(a.uploaded_at) - new Date(b.uploaded_at));
    } else if (sortOrder === 'title') {
      filtered.sort((a, b) => a.title.localeCompare(b.title));
    }

    return filtered;
  };

  const handleBulkDelete = async () => {
    if (selectedFilings.length === 0) return;

    const confirmed = window.confirm(
      `Delete ${selectedFilings.length} document(s)? This action cannot be undone.`
    );
    if (!confirmed) return;

    setUploading(true); // Reuse uploading state for bulk delete loading
    let successCount = 0;
    let errorCount = 0;

    for (const filing of selectedFilings) {
      const { error: storageError } = await supabase.storage
        .from('filings')
        .remove([filing.file_path]);

      if (storageError) {
        errorCount++;
        continue;
      }

      const { error: dbError } = await supabase.from('filings').delete().eq('id', filing.id);
      if (dbError) {
        errorCount++;
      } else {
        successCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`Deleted ${successCount} document(s)`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to delete ${errorCount} document(s)`);
    }

    setSelectedFilings([]);
    setUploading(false);
    loadFilings(user.id);
  };

  const toggleFilingSelection = (filing) => {
    setSelectedFilings((prev) =>
      prev.some((f) => f.id === filing.id)
        ? prev.filter((f) => f.id !== filing.id)
        : [...prev, filing]
    );
  };

  const selectAllFilings = () => {
    const displayFilings = filteredAndSortedFilings();
    if (selectedFilings.length === displayFilings.length) {
      setSelectedFilings([]);
    } else {
      setSelectedFilings(displayFilings);
    }
  };

  // Login screen
  if (!user) {
    return (
      <>
        <PortalHeader user={null} />
        <div style={styles.container}>
          <div style={styles.content}>
          <Card style={{ maxWidth: '500px', margin: '4rem auto' }}>
            <h1 style={{ ...styles.header, fontFamily: fonts.heading, fontSize: '2rem', marginBottom: spacing.md }}>
              Business Initiative Client Portal
            </h1>
            <p style={{ color: colors.midGray, marginBottom: spacing.xl, lineHeight: 1.6 }}>
              {showForgotPassword
                ? 'Enter the email address for your account. We will send you a link to choose a new password.'
                : 'Sign in to access your business filings and documents securely.'}
            </p>
            <Input
              type="email"
              label="Email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                // Clear error when user types
                if (emailError) {
                  setEmailError('');
                }
              }}
              onBlur={() => {
                // Validate email on blur
                if (email.trim()) {
                  const validation = validateEmailField(email, false);
                  if (!validation.isValid) {
                    setEmailError(validation.error);
                  } else {
                    setEmailError('');
                  }
                }
              }}
              placeholder="Email"
              error={emailError}
              required
            />
            {!showForgotPassword && (
              <>
                <Input
                  type="password"
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password (min 12 characters)"
                  required
                  helperText="Minimum 12 characters required"
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: spacing.xs }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(true);
                      setEmailError('');
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      color: colors.blue,
                      fontSize: '0.875rem',
                      fontFamily: 'inherit',
                      textDecoration: 'none',
                    }}
                  >
                    Forgot password?
                  </button>
                </div>
              </>
            )}
            {TURNSTILE_SITEKEY && (
              <div style={{ marginTop: spacing.md, marginBottom: spacing.md }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: spacing.xs, minHeight: '65px' }}>
                  <Turnstile
                    siteKey={TURNSTILE_SITEKEY}
                    onSuccess={(token) => {
                      console.log('✅ Turnstile token received:', token);
                      setCaptchaToken(token);
                      toast.success('CAPTCHA verified');
                    }}
                    onError={(error) => {
                      console.error('❌ Turnstile error:', error);
                      toast.error('CAPTCHA verification failed. Please try again.');
                      setCaptchaToken(null);
                    }}
                    onExpire={() => {
                      console.log('⏰ Turnstile token expired');
                      setCaptchaToken(null);
                      toast.info('CAPTCHA expired. Please verify again.');
                    }}
                    onLoad={() => {
                      console.log('🔄 Turnstile widget loaded');
                    }}
                  />
                </div>
                {!captchaToken && (
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: '0.875rem', color: colors.midGray, marginTop: spacing.xs }}>
                      Please complete the CAPTCHA above to continue
                    </p>
                    <p style={{ fontSize: '0.75rem', color: colors.gray400, marginTop: spacing.xs }}>
                      If CAPTCHA is disabled in Supabase, you can test without it
                    </p>
                  </div>
                )}
                {captchaToken && (
                  <p style={{ fontSize: '0.875rem', color: colors.success, marginTop: spacing.xs, textAlign: 'center', fontWeight: 500 }}>
                    ✓ CAPTCHA verified
                  </p>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: spacing.md, marginTop: spacing.lg, flexWrap: 'wrap' }}>
              {showForgotPassword ? (
                <>
                  <Button
                    onClick={sendPasswordResetEmail}
                    variant="primary"
                    fullWidth
                    disabled={sendingResetEmail || signingIn || signingUp}
                  >
                    {sendingResetEmail ? 'Sending…' : 'Send reset link'}
                  </Button>
                  <Button
                    onClick={() => {
                      setShowForgotPassword(false);
                      setSendingResetEmail(false);
                    }}
                    variant="outline"
                    fullWidth
                    disabled={sendingResetEmail}
                  >
                    Back to sign in
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={signIn}
                    variant="primary"
                    fullWidth
                    disabled={signingIn || signingUp || sendingResetEmail}
                  >
                    {signingIn ? 'Logging in...' : 'Log In'}
                  </Button>
                  <Button
                    onClick={signUp}
                    variant="outline"
                    fullWidth
                    disabled={signingUp || signingIn || sendingResetEmail}
                  >
                    {signingUp ? 'Signing up...' : 'Sign Up'}
                  </Button>
                </>
              )}
            </div>
            <p style={{ color: colors.midGray, fontSize: '0.875rem', marginTop: spacing.lg, textAlign: 'center' }}>
              Need help?{' '}
              <a
                href="https://businessinitiative.org/contact/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: colors.blue, textDecoration: 'none' }}
              >
                Contact us
              </a>
            </p>
          </Card>
        </div>
        <PortalFooter />
      </div>
      </>
    );
  }

  // Set new password after following email reset link
  if (user && completingPasswordRecovery) {
    return (
      <>
        <PortalHeader user={user} showAdminLink={isAdmin} />
        <div style={styles.container}>
          <div style={styles.content}>
            <Card style={{ maxWidth: '500px', margin: '4rem auto' }}>
              <h1 style={{ ...styles.header, fontFamily: fonts.heading, fontSize: '2rem', marginBottom: spacing.md }}>
                Choose a new password
              </h1>
              <p style={{ color: colors.midGray, marginBottom: spacing.xl, lineHeight: 1.6 }}>
                You&apos;re signed in as <strong style={{ fontWeight: 600 }}>{user.email}</strong>. Enter and confirm your new password
                below.
              </p>
              <Input
                type="password"
                label="New password"
                value={recoveryPassword}
                onChange={(e) => {
                  setRecoveryPassword(e.target.value);
                  if (recoveryPasswordError) setRecoveryPasswordError('');
                }}
                placeholder="New password (min 12 characters)"
                required
                helperText="Minimum 12 characters required"
              />
              <Input
                type="password"
                label="Confirm new password"
                value={recoveryPasswordConfirm}
                onChange={(e) => {
                  setRecoveryPasswordConfirm(e.target.value);
                  if (recoveryPasswordError) setRecoveryPasswordError('');
                }}
                placeholder="Confirm new password"
                required
              />
              {recoveryPasswordError ? (
                <p style={{ color: colors.error, fontSize: '0.875rem', marginTop: spacing.sm }} role="alert">
                  {recoveryPasswordError}
                </p>
              ) : null}
              <div style={{ display: 'flex', gap: spacing.md, marginTop: spacing.lg, flexWrap: 'wrap' }}>
                <Button onClick={completePasswordRecovery} variant="primary" fullWidth disabled={updatingRecoveryPassword}>
                  {updatingRecoveryPassword ? 'Updating…' : 'Update password and continue'}
                </Button>
                <Button onClick={() => signOut()} variant="outline" fullWidth disabled={updatingRecoveryPassword}>
                  Cancel
                </Button>
              </div>
            </Card>
          </div>
          <PortalFooter />
        </div>
      </>
    );
  }

  // Loading state
  if (profileLoading) {
    return (
      <>
        <PortalHeader user={user} showAdminLink={isAdmin} />
        <div style={styles.container}>
          <div style={styles.content}>
            <Card>
              <SkeletonLoader variant="form" count={3} />
            </Card>
          </div>
          <PortalFooter />
        </div>
      </>
    );
  }

  // Profile setup screen removed — incomplete stubs are created automatically.

  const displayFilings = filteredAndSortedFilings();
  const uniqueTypes = [...new Set(filings.map((f) => f.document_type).filter(Boolean))];

  // Get unique tags for autocomplete
  const existingTags = [...new Set(filings.map((f) => f.tag).filter(Boolean))];
  
  // Calculate dashboard stats
  const dashboardStats = {
    totalDocuments: filings.length,
    recentUploads: filings.filter((f) => {
      const uploadDate = new Date(f.uploaded_at);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return uploadDate >= sevenDaysAgo;
    }).length,
    documentsByType: uniqueTypes.reduce((acc, type) => {
      acc[type] = filings.filter((f) => f.document_type === type).length;
      return acc;
    }, {}),
    totalFolders: folders.length,
  };

  const tableColumns = [
    {
      key: 'title',
      label: 'Title',
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
          <input
            type="checkbox"
            checked={selectedFilings.some((f) => f.id === row.id)}
            onChange={() => toggleFilingSelection(row)}
            aria-label={`Select ${value}`}
            style={{ cursor: 'pointer' }}
          />
          <Icon name={row.document_type || 'Other'} type="document" size={18} style={{ color: colors.midGray }} />
          <span>{value}</span>
        </div>
      ),
    },
    {
      key: 'document_type',
      label: 'Type',
      render: (value) =>
        value ? (
          <span style={styles.badge}>
            <Icon name={value} type="document" size={14} style={{ marginRight: spacing.xs, verticalAlign: 'middle' }} />
            {value}
          </span>
        ) : (
          <span style={{ color: colors.lightGray }}>—</span>
        ),
    },
    {
      key: 'tag',
      label: 'Tag/Note',
      render: (value) =>
        value ? (
          <span style={{ ...styles.badge, backgroundColor: colors.blue, color: colors.white }}>
            {value}
          </span>
        ) : (
          <span style={{ color: colors.lightGray }}>—</span>
        ),
    },
    {
      key: 'custom_date',
      label: 'Custom Date',
      render: (value) =>
        value ? (
          <span>{new Date(value).toLocaleDateString()}</span>
        ) : (
          <span style={{ color: colors.lightGray }}>—</span>
        ),
    },
    {
      key: 'uploaded_at',
      label: 'Uploaded',
      render: (value) => new Date(value).toLocaleDateString(),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleEditClick(row)}
            icon={<Icon name="edit" type="action" size={16} />}
            aria-label={`Edit ${row.title}`}
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={async () => {
              const url = await getDownloadUrl(row.file_path);
              if (url) window.open(url, '_blank');
            }}
            icon={<Icon name="download" type="action" size={16} />}
            aria-label={`Download ${row.title}`}
          >
            Download
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => handleDeleteClick(row)}
            icon={<Icon name="delete" type="action" size={16} />}
            aria-label={`Delete ${row.title}`}
          >
            Delete
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handlePreview(row)}
            icon={<Icon name="document" type="document" size={16} />}
            aria-label={`Preview ${row.title}`}
          >
            Preview
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PortalHeader user={user} showAdminLink={isAdmin} />
      <div style={styles.container}>
        <div style={styles.content}>
        <div style={styles.header}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: spacing.md,
            }}
          >
            <div>
              <h1 style={{ fontFamily: fonts.heading, fontSize: '2rem', margin: 0 }}>
                {clientProfile?.business_name || 'Welcome to your portal'}
              </h1>
              <p style={{ color: colors.midGray, margin: 0 }}>
                {clientProfile?.entity_type ? `${clientProfile.entity_type} • ` : ''}
                {user.email}
              </p>
            </div>
            <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button onClick={signOut} variant="ghost" icon={<Icon name="user" type="action" size={18} />}>
                Logout
              </Button>
            </div>
          </div>
        </div>

        <PortalNav />

        {portalSection === 'home' && (
          <>
            <Card title="Your dashboard" variant="gradient" hoverable={false} style={{ marginBottom: spacing.xl }}>
              {!clientProfile?.business_name ? (
                <EmptyState
                  icon="document"
                  iconType="document"
                  title="You're on the platform"
                  description="You can request a service anytime, or wait while we connect your existing company to this account. No company profile is required to stay signed in."
                  actionLabel="Request Services"
                  onAction={() => navigate('/request-services')}
                />
              ) : (
                <p style={{ color: colors.midGray, margin: 0, lineHeight: 1.6 }}>
                  Manage documents, review company information on file, and check billing from the tabs above.
                </p>
              )}
            </Card>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: spacing.md,
                marginBottom: spacing.xl,
              }}
            >
              <Card variant="gradient" hoverable={false} style={{ padding: spacing.lg, textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 600, color: colors.blue, marginBottom: spacing.xs }}>
                  {dashboardStats.totalDocuments}
                </div>
                <div style={{ color: colors.midGray, fontSize: '0.9rem' }}>Documents</div>
              </Card>
              <Card variant="gradient" hoverable={false} style={{ padding: spacing.lg, textAlign: 'center' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 600, color: colors.darkGray, marginBottom: spacing.xs }}>
                  {clientProfile?.nwra_company_id ? 'NWRA linked' : 'NWRA pending'}
                </div>
                <div style={{ color: colors.midGray, fontSize: '0.9rem' }}>
                  {Array.isArray(clientProfile?.nwra_deadlines) && clientProfile.nwra_deadlines.length > 0
                    ? `${clientProfile.nwra_deadlines.length} deadline(s)`
                    : 'My status (NWRA)'}
                </div>
              </Card>
              <Card
                variant="gradient"
                hoverable={false}
                style={{ padding: spacing.lg, textAlign: 'center', cursor: 'pointer' }}
                onClick={() => navigate('/company')}
              >
                <div style={{ fontSize: '1.1rem', fontWeight: 600, color: colors.darkGray, marginBottom: spacing.xs }}>
                  {clientProfile?.account_state === 'dormant' ? 'Activating…' : clientProfile?.business_name ? 'Active' : 'Incomplete'}
                </div>
                <div style={{ color: colors.midGray, fontSize: '0.9rem' }}>Company profile</div>
              </Card>
              <Card
                variant="gradient"
                hoverable={false}
                style={{ padding: spacing.lg, textAlign: 'center', cursor: 'pointer' }}
                onClick={() => navigate('/billing')}
              >
                <div style={{ fontSize: '1.1rem', fontWeight: 600, color: colors.blue, marginBottom: spacing.xs }}>
                  View billing →
                </div>
                <div style={{ color: colors.midGray, fontSize: '0.9rem' }}>Stripe payments</div>
              </Card>
            </div>
          </>
        )}

        {portalSection === 'billing' && (
          <Card title="Billing & payments" variant="gradient" hoverable={false} style={{ marginBottom: spacing.xl }}>
            <p style={{ color: colors.midGray, marginBottom: spacing.lg, lineHeight: 1.6 }}>
              Combined read-only history from Business Initiative Stripe accounts (direct + JotForm), matched to your email.
            </p>
            {billingLoading ? (
              <SkeletonLoader variant="card" count={2} />
            ) : billingError ? (
              <EmptyState
                icon="document"
                iconType="document"
                title="Billing unavailable"
                description={`${billingError} If this is unexpected, contact support — Stripe keys may not be configured yet.`}
                actionLabel="Retry"
                onAction={loadBilling}
              />
            ) : (
              <>
                {billingData?.connections && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: spacing.md,
                      marginBottom: spacing.lg,
                    }}
                  >
                    {['direct', 'jotform'].map((key) => {
                      const conn = billingData.connections[key] || {}
                      const label = key === 'jotform' ? 'JotForm Stripe' : 'Direct Stripe'
                      return (
                        <div
                          key={key}
                          style={{
                            padding: spacing.md,
                            border: `1px solid ${colors.gray200 || '#e5e7eb'}`,
                            borderRadius: layout.borderRadius,
                            backgroundColor: conn.active ? '#f0fdf4' : '#fef2f2',
                          }}
                        >
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
                          <div style={{ fontSize: '0.875rem', color: colors.midGray }}>
                            {conn.active
                              ? `Connected${conn.livemode === false ? ' (test mode)' : ''}`
                              : conn.error || 'Not connected'}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
                {(billingData?.warnings || []).length > 0 && (
                  <p style={{ color: colors.midGray, fontSize: '0.875rem', marginBottom: spacing.md }}>
                    {billingData.warnings.join(' · ')}
                  </p>
                )}
                {!(billingData?.payments || []).length && !(billingData?.subscriptions || []).length ? (
              <EmptyState
                icon="document"
                iconType="document"
                title="No payments found yet"
                description={
                  billingData?.customers_found &&
                  !Object.values(billingData.customers_found).some((list) => (list || []).length)
                    ? `No Stripe customer matched ${billingData.lookup_emails?.join(' / ') || billingData.email || 'your email'}. Confirm the live customer email matches your portal login, or ask an admin to pin the Stripe customer ID.`
                    : `When you purchase through our forms or subscriptions, charges linked to your email will appear here.${
                        (billingData?.warnings || []).length
                          ? ` (${billingData.warnings.slice(0, 2).join(' · ')})`
                          : ''
                      }`
                }
                actionLabel="Request Services"
                onAction={() => navigate('/request-services')}
              />
            ) : (
              <>
                {(billingData.subscriptions || []).length > 0 && (
                  <div style={{ marginBottom: spacing.xl }}>
                    <h3 style={{ fontFamily: fonts.heading, fontSize: '1.1rem', marginBottom: spacing.md }}>Subscriptions</h3>
                    <Table
                      columns={[
                        { key: 'status', label: 'Status' },
                        { key: 'account', label: 'Account', render: (v) => (v === 'jotform' ? 'JotForm Stripe' : 'Direct Stripe') },
                        { key: 'plan', label: 'Plan', render: (v) => v || '—' },
                        { key: 'current_period_end', label: 'Renews', render: (v) => (v ? new Date(v * 1000).toLocaleDateString() : '—') },
                      ]}
                      data={billingData.subscriptions}
                      keyField="id"
                      responsive
                    />
                  </div>
                )}
                <h3 style={{ fontFamily: fonts.heading, fontSize: '1.1rem', marginBottom: spacing.md }}>Payment history</h3>
                <Table
                  columns={[
                    { key: 'occurred_at', label: 'Date', render: (v) => (v ? new Date(v).toLocaleDateString() : '—') },
                    { key: 'description', label: 'Description', render: (v) => v || 'Payment' },
                    {
                      key: 'amount_cents',
                      label: 'Amount',
                      render: (v, row) =>
                        v != null
                          ? new Intl.NumberFormat('en-US', { style: 'currency', currency: (row.currency || 'usd').toUpperCase() }).format(v / 100)
                          : '—',
                    },
                    { key: 'status', label: 'Status' },
                    { key: 'account', label: 'Source', render: (v) => (v === 'jotform' ? 'JotForm' : 'Direct') },
                  ]}
                  data={billingData.payments || []}
                  keyField="id"
                  responsive
                />
              </>
            )}
              </>
            )}
          </Card>
        )}

        {portalSection === 'documents' && (
          <>
        {!clientProfile?.business_name && filings.length === 0 && (
          <EmptyState
            icon="document"
            iconType="document"
            title="No documents linked yet"
            description="Once your company is linked or you upload filings, they will appear here. You can still request services anytime."
            actionLabel="Request Services"
            onAction={() => navigate('/request-services')}
          />
        )}
        {filings.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: spacing.md,
              marginBottom: spacing.xl,
            }}
          >
            <Card
              variant="gradient"
              hoverable={false}
              style={{
                padding: spacing.lg,
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '2.5rem', fontWeight: 600, color: colors.blue, marginBottom: spacing.xs }}>
                {dashboardStats.totalDocuments}
              </div>
              <div style={{ color: colors.midGray, fontSize: '0.9rem' }}>Total Documents</div>
            </Card>

            <Card
              variant="gradient"
              hoverable={false}
              style={{
                padding: spacing.lg,
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '2.5rem', fontWeight: 600, color: colors.blue, marginBottom: spacing.xs }}>
                {dashboardStats.recentUploads}
              </div>
              <div style={{ color: colors.midGray, fontSize: '0.9rem' }}>Uploaded This Week</div>
            </Card>
            <Card
              variant="gradient"
              hoverable={false}
              style={{
                padding: spacing.lg,
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '2.5rem', fontWeight: 600, color: colors.blue, marginBottom: spacing.xs }}>
                {uniqueTypes.length}
              </div>
              <div style={{ color: colors.midGray, fontSize: '0.9rem' }}>Document Types</div>
            </Card>
            {dashboardStats.totalFolders > 0 && (
              <Card
                variant="gradient"
                hoverable={false}
                style={{
                  padding: spacing.lg,
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '2.5rem', fontWeight: 600, color: colors.blue, marginBottom: spacing.xs }}>
                  {dashboardStats.totalFolders}
                </div>
                <div style={{ color: colors.midGray, fontSize: '0.9rem' }}>Folders</div>
              </Card>
            )}
          </div>
        )}

        <Card
          title="Your Filings"
          variant="gradient"
          header={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: spacing.md }}>
              <h2 style={{ fontFamily: fonts.heading, fontSize: '1.5rem', fontWeight: 600, color: colors.darkGray, margin: 0 }}>
                Your Filings
              </h2>
              <div style={styles.filterBar}>
                <Button
                  size="sm"
                  variant={uploadFormOpen ? 'primary' : 'outline'}
                  onClick={() => setUploadFormOpen(!uploadFormOpen)}
                  icon={<Icon name="upload" type="action" size={16} />}
                >
                  {uploadFormOpen ? 'Hide Upload' : 'Upload Filing'}
                </Button>
                {filings.length > 0 && (
                  <>
                    <Button
                      size="sm"
                      variant={selectedFilings.length > 0 ? 'primary' : 'outline'}
                      onClick={selectAllFilings}
                      icon={<Icon name="document" type="document" size={16} />}
                    >
                      {selectedFilings.length === displayFilings.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    {selectedFilings.length > 0 && (
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={handleBulkDelete}
                        icon={<Icon name="delete" type="action" size={16} />}
                      >
                        Delete Selected ({selectedFilings.length})
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          }
        >
          {/* Upload Form Dropdown */}
          {uploadFormOpen && (
            <div style={{ marginBottom: spacing.xl, padding: spacing.lg, backgroundColor: colors.gray50, borderRadius: '19px', border: `1px solid ${colors.gray200}` }}>
              <h3 style={{ fontFamily: fonts.heading, fontSize: '1.1rem', fontWeight: 600, color: colors.darkGray, marginTop: 0, marginBottom: spacing.md }}>
                Upload New Filing
              </h3>
              <Input
                label="Document title"
                placeholder="Document title (e.g., Articles of Organization)"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
              <div style={{ marginTop: spacing.md, marginBottom: spacing.md }}>
                <label
                  style={{
                    display: 'block',
                    fontFamily: fonts.heading,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    marginBottom: spacing.xs,
                    color: colors.darkGray,
                  }}
                >
                  Document type (optional)
                </label>
                <select
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  style={styles.select}
                >
                  <option value="">Select document type</option>
                  {DOCUMENT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: spacing.md,
                  marginBottom: spacing.md,
                }}
              >
                <div style={{ position: 'relative' }}>
                  <Input
                    label="Tag/Note (optional, max 30 chars)"
                    placeholder="e.g., Tax 2024, Q1 Report"
                    value={tag}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 30) {
                        setTag(value);
                        // Show suggestions based on input
                        if (value.trim() && existingTags.length > 0) {
                          const filtered = existingTags.filter(t => 
                            t.toLowerCase().includes(value.toLowerCase())
                          );
                          setTagSuggestions(filtered.slice(0, 5));
                          setShowTagSuggestions(filtered.length > 0);
                        } else {
                          setShowTagSuggestions(false);
                        }
                      }
                    }}
                    onFocus={() => {
                      if (tag.trim() && existingTags.length > 0) {
                        const filtered = existingTags.filter(t => 
                          t.toLowerCase().includes(tag.toLowerCase())
                        );
                        setTagSuggestions(filtered.slice(0, 5));
                        setShowTagSuggestions(filtered.length > 0);
                      } else if (existingTags.length > 0) {
                        setTagSuggestions(existingTags.slice(0, 5));
                        setShowTagSuggestions(true);
                      }
                    }}
                    onBlur={() => {
                      // Delay to allow click on suggestion
                      setTimeout(() => setShowTagSuggestions(false), 200);
                    }}
                    helperText={tag ? `${tag.length}/30 characters` : 'Creates a folder/category for easy lookup'}
                    maxLength={30}
                  />
                  {showTagSuggestions && tagSuggestions.length > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: colors.white,
                        border: `1px solid ${colors.borderColor}`,
                        borderRadius: layout.borderRadius,
                        boxShadow: layout.boxShadow,
                        zIndex: 1000,
                        marginTop: spacing.xs,
                        maxHeight: '200px',
                        overflowY: 'auto',
                      }}
                    >
                      {tagSuggestions.map((suggestion, idx) => {
                        const count = filings.filter(f => f.tag === suggestion).length;
                        return (
                          <div
                            key={idx}
                            onClick={() => {
                              setTag(suggestion);
                              setShowTagSuggestions(false);
                            }}
                            style={{
                              padding: spacing.sm,
                              cursor: 'pointer',
                              borderBottom: idx < tagSuggestions.length - 1 ? `1px solid ${colors.gray200}` : 'none',
                              transition: 'background-color 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = colors.gray50;
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = colors.white;
                            }}
                          >
                            <div style={{ fontWeight: 500, color: colors.darkGray }}>{suggestion}</div>
                            <div style={{ fontSize: '0.75rem', color: colors.midGray }}>
                              {count} document{count !== 1 ? 's' : ''}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <Input
                  type="date"
                  label="Date (optional)"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  helperText="Custom date for this document"
                />
              </div>
              <FileUploadZone
                onFilesSelected={(selectedFiles) => setFiles(selectedFiles)}
                maxFiles={1}
                multiple={false}
              />
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div style={{ marginTop: spacing.md }}>
                  <div
                    style={{
                      width: '100%',
                      height: '8px',
                      backgroundColor: colors.gray200,
                      borderRadius: layout.borderRadius,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${uploadProgress}%`,
                        height: '100%',
                        background: gradients.blueToGreen,
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </div>
                  <p style={{ fontSize: '0.875rem', color: colors.midGray, marginTop: spacing.xs }}>
                    Uploading... {uploadProgress}%
                  </p>
                </div>
              )}
              <div style={{ marginTop: spacing.lg }}>
                <Button onClick={uploadFile} loading={uploading} disabled={!files.length || !title} variant="primary" fullWidth>
                  Upload Filing
                </Button>
              </div>
            </div>
          )}

          {filings.length > 0 && (
            <>
              <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search filings..." />
              <div style={styles.filterBar}>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  style={styles.select}
                  aria-label="Filter by document type"
                >
                  <option value="all">All types</option>
                  {uniqueTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  style={styles.select}
                  aria-label="Sort order"
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="title">Title (A-Z)</option>
                </select>
              </div>
            </>
          )}

          {filingsLoading ? (
            <SkeletonLoader variant="table" count={5} />
          ) : displayFilings.length === 0 ? (
            <EmptyState
              icon="document"
              iconType="document"
              title={filings.length === 0 ? 'No filings yet' : 'No matching filings'}
              description={
                filings.length === 0
                  ? 'Upload your first filing below to get started.'
                  : 'Try adjusting your search or filter criteria.'
              }
              actionLabel={filings.length === 0 ? 'Upload Filing' : 'Clear Filters'}
              onAction={filings.length === 0 ? () => {
                const uploadSection = document.querySelector('[data-upload-section]');
                if (uploadSection) {
                  uploadSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              } : () => {
                setSearchQuery('');
                setFilterType('all');
              }}
            />
          ) : (
            <Table columns={tableColumns} data={displayFilings} responsive />
          )}
        </Card>
        </>
        )}

        {portalSection === 'services' && (
        <Card 
          title="Request Services & Forms" 
          variant="gradient"
          style={{ marginBottom: spacing.xl }}
          hoverable={false}
        >
          <p style={{ color: colors.midGray, marginBottom: spacing.md, lineHeight: 1.6 }}>
            Need additional services, forms, or filings? Use the form below to request new services, purchase forms, or get assistance with your business needs.
          </p>
          <div style={{ 
            width: '100%', 
            minHeight: '600px',
            border: `1px solid ${colors.borderColor}`,
            borderRadius: layout.borderRadius,
            overflow: 'hidden',
            backgroundColor: colors.white
          }}>
            <iframe
              id="JotFormIFrame-252943654936065"
              title="Business Initiative Service Form"
              allowTransparency="true"
              allow="geolocation; microphone; camera; fullscreen; payment"
              src="https://form.jotform.com/252943654936065"
              frameBorder="0"
              style={{
                minWidth: '100%',
                maxWidth: '100%',
                height: '600px',
                border: 'none',
              }}
              scrolling="no"
            />
          </div>
        </Card>
        )}

        {portalSection === 'documents' && filings.length > 0 && (
          <div style={styles.guidanceBox}>
            <h3 style={{ fontFamily: fonts.heading, fontSize: '1.2rem', fontWeight: 600, color: colors.darkGray, marginTop: 0, marginBottom: spacing.sm }}>
              Next Steps
            </h3>
            <p style={{ color: colors.darkGray, margin: 0, lineHeight: 1.6 }}>
              Keep your documents up to date. Upload annual reports, tax filings, and compliance documents as they come
              due. Need help?{' '}
              <a
                href="https://businessinitiative.org/contact/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: colors.blue, textDecoration: 'none', fontWeight: 500 }}
              >
                Contact us
              </a>{' '}
              for assistance.
            </p>
          </div>
        )}

        {portalSection === 'company' && (
        <Card title="Company information" variant="gradient" hoverable={false}>
          {!clientProfile?.business_name ? (
            <EmptyState
              icon="document"
              iconType="document"
              title="No company linked yet"
              description="You can stay on the platform without a company. Request a service, or wait for an administrator to connect your company to this account."
              actionLabel="Request Services"
              onAction={() => navigate('/request-services')}
            />
          ) : null}
          {clientProfile?.nwra_synced_at && (
            <p style={{ fontSize: '0.8rem', color: colors.midGray, marginTop: 0, marginBottom: spacing.md }}>
              Records last updated {new Date(clientProfile.nwra_synced_at).toLocaleString()}
            </p>
          )}
          {Array.isArray(clientProfile?.nwra_deadlines) && clientProfile.nwra_deadlines.length > 0 && (
            <div style={{ marginBottom: spacing.xl }}>
              <h3 style={{ fontFamily: fonts.heading, fontSize: '1.1rem', marginBottom: spacing.sm }}>Upcoming deadlines</h3>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', color: colors.darkGray }}>
                {clientProfile.nwra_deadlines.map((d, i) => (
                  <li key={i} style={{ marginBottom: spacing.xs }}>
                    {typeof d === 'object' ? `${d.field || 'Deadline'}: ${String(d.value)}` : String(d)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p style={{ color: colors.midGray, marginBottom: spacing.lg, lineHeight: 1.6 }}>
            Keep your contact and business information up to date. All changes are tracked and recorded.
          </p>

          {/* Business Profile Section (Editable) */}
          <div style={{ marginBottom: spacing.xl }}>
            <h3 style={{ fontFamily: fonts.heading, fontSize: '1.25rem', fontWeight: 600, color: colors.darkGray, marginBottom: spacing.md, borderBottom: `2px solid ${colors.gray200}`, paddingBottom: spacing.xs }}>
              Business Profile
            </h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: spacing.md,
              }}
            >
              <Input
                label="Business name"
                placeholder="Legal business name"
                value={businessName}
                autoComplete="organization"
                onChange={(e) => {
                  setBusinessName(e.target.value);
                  if (validationErrors.businessName) {
                    setValidationErrors(prev => { const next = { ...prev }; delete next.businessName; return next; });
                  }
                }}
                error={validationErrors.businessName}
              />
              <EntityTypeSelect
                value={entityType}
                onChange={(e) => {
                  setEntityType(e.target.value);
                  if (validationErrors.entityType) {
                    setValidationErrors(prev => { const next = { ...prev }; delete next.entityType; return next; });
                  }
                }}
                error={validationErrors.entityType}
              />
            </div>
          </div>

          {/* Owner Information Section (Editable) */}
          <div style={{ marginBottom: spacing.xl }}>
            <h3 style={{ fontFamily: fonts.heading, fontSize: '1.25rem', fontWeight: 600, color: colors.darkGray, marginBottom: spacing.md, borderBottom: `2px solid ${colors.gray200}`, paddingBottom: spacing.xs }}>
              Owner Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: spacing.md, marginBottom: spacing.md }}>
              <Input
                label="Owner name"
                placeholder="Full name"
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
              />
              <Input
                type="email"
                label="Owner email"
                placeholder="email@example.com"
                value={ownerEmail}
                onChange={(e) => {
                  setOwnerEmail(e.target.value);
                  if (validationErrors.ownerEmail) {
                    setValidationErrors(prev => { const next = { ...prev }; delete next.ownerEmail; return next; });
                  }
                }}
                error={validationErrors.ownerEmail}
              />
              <Input
                type="tel"
                label="Owner phone"
                placeholder="(555) 123-4567"
                value={ownerPhone}
                onChange={(e) => {
                  setOwnerPhone(e.target.value);
                  if (validationErrors.ownerPhone) {
                    setValidationErrors(prev => { const next = { ...prev }; delete next.ownerPhone; return next; });
                  }
                }}
                error={validationErrors.ownerPhone}
              />
            </div>
            <Input
              label="Owner address line 1"
              placeholder="Street address"
              value={ownerAddressLine1}
              onChange={(e) => setOwnerAddressLine1(e.target.value)}
              style={{ marginBottom: spacing.sm }}
            />
            <Input
              label="Owner address line 2"
              placeholder="Apt, suite, etc. (optional)"
              value={ownerAddressLine2}
              onChange={(e) => setOwnerAddressLine2(e.target.value)}
              style={{ marginBottom: spacing.sm }}
            />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: spacing.sm }}>
              <Input
                label="City"
                placeholder="City"
                value={ownerCity}
                onChange={(e) => setOwnerCity(e.target.value)}
              />
              <Input
                label="State"
                placeholder="State"
                value={ownerState}
                onChange={(e) => setOwnerState(e.target.value)}
              />
              <Input
                label="ZIP code"
                placeholder="ZIP"
                value={ownerZip}
                onChange={(e) => setOwnerZip(e.target.value)}
              />
            </div>
          </div>

          {/* Business Information Section (Editable) */}
          <div style={{ marginBottom: spacing.xl }}>
            <h3 style={{ fontFamily: fonts.heading, fontSize: '1.25rem', fontWeight: 600, color: colors.darkGray, marginBottom: spacing.md, borderBottom: `2px solid ${colors.gray200}`, paddingBottom: spacing.xs }}>
              Business Information
            </h3>
            <Input
              label="Business address line 1"
              placeholder="Street address"
              value={businessAddressLine1}
              onChange={(e) => setBusinessAddressLine1(e.target.value)}
              style={{ marginBottom: spacing.sm }}
            />
            <Input
              label="Business address line 2"
              placeholder="Suite, unit, etc. (optional)"
              value={businessAddressLine2}
              onChange={(e) => setBusinessAddressLine2(e.target.value)}
              style={{ marginBottom: spacing.sm }}
            />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: spacing.sm, marginBottom: spacing.md }}>
              <Input
                label="City"
                placeholder="City"
                value={businessCity}
                onChange={(e) => setBusinessCity(e.target.value)}
              />
              <Input
                label="State"
                placeholder="State"
                value={businessState}
                onChange={(e) => setBusinessState(e.target.value)}
              />
              <Input
                label="ZIP code"
                placeholder="ZIP"
                value={businessZip}
                onChange={(e) => setBusinessZip(e.target.value)}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: spacing.md }}>
              <Input
                type="tel"
                label="Business phone"
                placeholder="(555) 123-4567"
                value={businessPhone}
                onChange={(e) => {
                  setBusinessPhone(e.target.value);
                  if (validationErrors.businessPhone) {
                    setValidationErrors(prev => { const next = { ...prev }; delete next.businessPhone; return next; });
                  }
                }}
                error={validationErrors.businessPhone}
              />
              <Input
                type="email"
                label="Business email"
                placeholder="business@example.com"
                value={businessEmail}
                onChange={(e) => {
                  setBusinessEmail(e.target.value);
                  if (validationErrors.businessEmail) {
                    setValidationErrors(prev => { const next = { ...prev }; delete next.businessEmail; return next; });
                  }
                }}
                error={validationErrors.businessEmail}
              />
              <Input
                label="EIN (Employer Identification Number)"
                placeholder="XX-XXXXXXX"
                value={ein}
                onChange={(e) => setEin(e.target.value)}
              />
              <Input
                label="Registration number"
                placeholder="State registration number"
                value={registrationNumber}
                onChange={(e) => setRegistrationNumber(e.target.value)}
              />
            </div>
          </div>

          {/* Registered Agent Information Section (Admin-only, read-only for users) */}
          {clientProfile && (
            clientProfile.registered_agent_name ||
            clientProfile.registered_agent_address_line1 ||
            clientProfile.registered_agent_phone ||
            clientProfile.registered_agent_email
          ) && (
            <div style={{ marginBottom: spacing.xl }}>
              <h3 style={{ fontFamily: fonts.heading, fontSize: '1.25rem', fontWeight: 600, color: colors.darkGray, marginBottom: spacing.md, borderBottom: `2px solid ${colors.gray200}`, paddingBottom: spacing.xs }}>
                Registered Agent Information
              </h3>
              <p style={{ fontSize: '0.875rem', color: colors.midGray, marginBottom: spacing.md, fontStyle: 'italic' }}>
                This information is managed by your administrator.
              </p>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: spacing.md,
                  marginBottom: spacing.md,
                }}
              >
                {clientProfile.registered_agent_name && (
                  <div>
                    <label style={{ display: 'block', fontFamily: fonts.heading, fontSize: '0.9rem', fontWeight: 500, marginBottom: spacing.xs, color: colors.darkGray }}>
                      Registered Agent Name
                    </label>
                    <div style={{ padding: spacing.sm, backgroundColor: colors.gray100, borderRadius: '19px', color: colors.darkGray, minHeight: '2.5rem', display: 'flex', alignItems: 'center' }}>
                      {clientProfile.registered_agent_name}
                    </div>
                  </div>
                )}
                {clientProfile.registered_agent_phone && (
                  <div>
                    <label style={{ display: 'block', fontFamily: fonts.heading, fontSize: '0.9rem', fontWeight: 500, marginBottom: spacing.xs, color: colors.darkGray }}>
                      Registered Agent Phone
                    </label>
                    <div style={{ padding: spacing.sm, backgroundColor: colors.gray100, borderRadius: '19px', color: colors.darkGray, minHeight: '2.5rem', display: 'flex', alignItems: 'center' }}>
                      {clientProfile.registered_agent_phone}
                    </div>
                  </div>
                )}
                {clientProfile.registered_agent_email && (
                  <div>
                    <label style={{ display: 'block', fontFamily: fonts.heading, fontSize: '0.9rem', fontWeight: 500, marginBottom: spacing.xs, color: colors.darkGray }}>
                      Registered Agent Email
                    </label>
                    <div style={{ padding: spacing.sm, backgroundColor: colors.gray100, borderRadius: '19px', color: colors.darkGray, minHeight: '2.5rem', display: 'flex', alignItems: 'center' }}>
                      {clientProfile.registered_agent_email}
                    </div>
                  </div>
                )}
              </div>
              {(clientProfile.registered_agent_address_line1 || clientProfile.registered_agent_city) && (
                <div style={{ marginTop: spacing.md }}>
                  <label style={{ display: 'block', fontFamily: fonts.heading, fontSize: '0.9rem', fontWeight: 500, marginBottom: spacing.xs, color: colors.darkGray }}>
                    Registered Agent Address
                  </label>
                  <div style={{ padding: spacing.sm, backgroundColor: colors.gray100, borderRadius: '19px', color: colors.darkGray, minHeight: '2.5rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    {clientProfile.registered_agent_address_line1 && (
                      <div>{clientProfile.registered_agent_address_line1}</div>
                    )}
                    {clientProfile.registered_agent_address_line2 && (
                      <div>{clientProfile.registered_agent_address_line2}</div>
                    )}
                    {(clientProfile.registered_agent_city || clientProfile.registered_agent_state || clientProfile.registered_agent_zip) && (
                      <div>
                        {[
                          clientProfile.registered_agent_city,
                          clientProfile.registered_agent_state,
                          clientProfile.registered_agent_zip
                        ].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {clientProfile?.last_info_update && (
            <div style={{ marginTop: spacing.lg, padding: spacing.md, backgroundColor: colors.gray100, borderRadius: '19px' }}>
              <p style={{ fontSize: '0.875rem', color: colors.midGray, margin: 0 }}>
                <strong>Last updated:</strong> {new Date(clientProfile.last_info_update).toLocaleString()}
                {clientProfile.info_update_requested_at && (
                  <span style={{ display: 'block', marginTop: spacing.xs }}>
                    <strong>Change requested:</strong> {new Date(clientProfile.info_update_requested_at).toLocaleString()}
                  </span>
                )}
              </p>
            </div>
          )}

          {profileError && (
            <p style={{ color: colors.error, marginTop: spacing.md, marginBottom: spacing.md }}>{profileError}</p>
          )}

          <Button
            onClick={saveClientProfile}
            loading={savingProfile}
            variant="primary"
            style={{ marginTop: spacing.sm }}
            disabled={profileSaveCooldownSeconds > 0}
          >
            {profileSaveCooldownSeconds > 0
              ? `Save changes (wait ${profileSaveCooldownSeconds}s)`
              : 'Save changes'}
          </Button>
          {profileSaveCooldownSeconds > 0 && (
            <p style={{ fontSize: '0.875rem', color: colors.midGray, marginTop: spacing.xs, marginBottom: 0 }}>
              Please wait before saving again to avoid overwhelming the system.
            </p>
          )}

          <div style={{ marginTop: spacing.xl, paddingTop: spacing.lg, borderTop: `1px solid ${colors.gray200}` }}>
            <p style={{ fontSize: '0.875rem', color: colors.midGray, marginBottom: spacing.sm }}>Permanently remove your account and profile.</p>
            <Button variant="outline" onClick={() => setShowDeleteAccountModal(true)} style={{ borderColor: colors.error, color: colors.error }}>
              Delete my account
            </Button>
          </div>
        </Card>
        )}
        </div>
        <PortalFooter />
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setFilingToDelete(null);
        }}
        title="Delete Document"
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={deleteFiling}>
              Delete
            </Button>
          </>
        }
      >
        <p>Are you sure you want to delete "{filingToDelete?.title}"? This action cannot be undone.</p>
      </Modal>

      {/* Confirm password before profile save */}
      <Modal
        isOpen={showConfirmPasswordModal}
        onClose={() => {
          setShowConfirmPasswordModal(false);
          setConfirmPassword('');
          setConfirmPasswordError('');
        }}
        title="Confirm your identity"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowConfirmPasswordModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirmPasswordSubmit} loading={savingProfile}>
              Confirm and save
            </Button>
          </>
        }
      >
        <p style={{ marginBottom: spacing.md }}>Enter your password to confirm these profile changes.</p>
        <Input
          type="password"
          label="Password"
          placeholder="Your password"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            if (confirmPasswordError) setConfirmPasswordError('');
          }}
          error={confirmPasswordError}
        />
        <p style={{ marginTop: spacing.sm, fontSize: '0.875rem', color: colors.midGray }}>
          Use magic link to sign in? <button type="button" onClick={handleSaveWithoutConfirm} style={{ background: 'none', border: 'none', color: colors.primary || '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}>Save without confirming</button>
        </p>
      </Modal>

      {/* Delete account confirmation */}
      <Modal
        isOpen={showDeleteAccountModal}
        onClose={() => !deletingAccount && setShowDeleteAccountModal(false)}
        title="Delete my account"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowDeleteAccountModal(false)} disabled={deletingAccount}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteAccount} loading={deletingAccount}>
              {deletingAccount ? 'Deleting...' : 'Delete account'}
            </Button>
          </>
        }
      >
        <p style={{ marginBottom: spacing.md }}>
          This will remove your account and profile. We will keep a record of your profile and changes for our records. You will not be able to log in again.
        </p>
        <p style={{ fontSize: '0.875rem', color: colors.midGray }}>Are you sure you want to continue?</p>
      </Modal>

      {/* Preview Modal */}
      <Modal
        isOpen={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setPreviewFile(null);
        }}
        title={previewFile?.fileName}
        size="large"
      >
        {previewFile && (
          <DocumentPreview
            fileUrl={previewFile.url}
            fileName={previewFile.fileName}
            fileType={previewFile.fileType}
            fileExtension={previewFile.fileExtension}
            onDownload={async () => {
              window.open(previewFile.url, '_blank');
            }}
            onClose={() => setPreviewModalOpen(false)}
          />
        )}
      </Modal>

      {/* Edit Document Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingFiling(null);
          setEditTitle('');
          setEditDocumentType('');
          setEditTag('');
          setEditCustomDate('');
          setEditSelectedFolderId(null);
        }}
        title="Edit Document"
        size="medium"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setEditModalOpen(false);
                setEditingFiling(null);
                setEditTitle('');
                setEditDocumentType('');
                setEditTag('');
                setEditCustomDate('');
                setEditSelectedFolderId(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={updateFiling} loading={updatingFiling}>
              Save Changes
            </Button>
          </>
        }
      >
        {editingFiling && (
          <div>
            <p style={{ color: colors.midGray, marginBottom: spacing.lg, lineHeight: 1.6 }}>
              Update the document details below. The uploaded date cannot be changed.
            </p>
            <Input
              label="Document title"
              placeholder="Document title"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              required
            />
            <div style={{ marginTop: spacing.md, marginBottom: spacing.md }}>
              <label
                style={{
                  display: 'block',
                  fontFamily: fonts.heading,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  marginBottom: spacing.xs,
                  color: colors.darkGray,
                }}
              >
                Document type (optional)
              </label>
              <select
                value={editDocumentType}
                onChange={(e) => setEditDocumentType(e.target.value)}
                style={styles.select}
              >
                <option value="">Select document type</option>
                {DOCUMENT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: spacing.md,
                marginBottom: spacing.md,
              }}
            >
              <Input
                label="Tag/Note (optional, max 30 chars)"
                placeholder="e.g., Tax 2024, Q1 Report"
                value={editTag}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 30) {
                    setEditTag(value);
                  }
                }}
                helperText={editTag ? `${editTag.length}/30 characters` : 'Creates a folder/category for easy lookup'}
                maxLength={30}
              />
              <Input
                type="date"
                label="Custom Date (optional)"
                value={editCustomDate}
                onChange={(e) => setEditCustomDate(e.target.value)}
                helperText="Custom date for this document"
              />
            </div>
            {folders.length > 0 && (
              <div style={{ marginTop: spacing.md, marginBottom: spacing.md }}>
                <label
                  style={{
                    display: 'block',
                    fontFamily: fonts.heading,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    marginBottom: spacing.xs,
                    color: colors.darkGray,
                  }}
                >
                  Folder (optional)
                </label>
                <select
                  value={editSelectedFolderId || ''}
                  onChange={(e) => setEditSelectedFolderId(e.target.value ? parseInt(e.target.value) : null)}
                  style={styles.select}
                >
                  <option value="">No folder</option>
                  {folders.map((folder) => (
                    <option key={folder.id} value={folder.id}>
                      {folder.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ marginTop: spacing.lg, padding: spacing.md, backgroundColor: colors.gray100, borderRadius: layout.borderRadius }}>
              <p style={{ fontSize: '0.875rem', color: colors.midGray, margin: 0 }}>
                <strong>Uploaded:</strong> {new Date(editingFiling.uploaded_at).toLocaleString()}
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* Floating Action Button for Quick Upload */}
      {user && portalSection === 'company' && (
        <button
          onClick={() => {
            setUploadFormOpen(true);
            setTimeout(() => {
              const titleInput = document.querySelector('input[placeholder*="Document title"]');
              if (titleInput) {
                titleInput.focus();
                window.scrollTo({ top: titleInput.offsetTop - 100, behavior: 'smooth' });
              }
            }, 100);
          }}
          style={{
            position: 'fixed',
            bottom: spacing.xl,
            right: spacing.xl,
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: colors.blue,
            color: colors.white,
            border: 'none',
            boxShadow: '0px 4px 14px 0px rgba(0, 99, 160, 0.3)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            transition: 'all 0.2s ease',
            fontSize: '24px',
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1)';
            e.target.style.boxShadow = '0px 6px 20px 0px rgba(0, 99, 160, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0px 4px 14px 0px rgba(0, 99, 160, 0.3)';
          }}
          aria-label="Quick upload document"
          title="Quick upload (Ctrl/Cmd + U)"
        >
          <Icon name="upload" type="action" size={28} style={{ color: colors.white }} />
        </button>
      )}
      </>
  );
}

export default Portal;
